import { createContext, useContext, useEffect, type ReactNode } from "react";
import {
  displayUnitOf,
  type ThemePreference,
  type UnitsPreference,
} from "@heraion/shared";
import { useThemePreferenceContext } from "@heraion/ui";
import { useProfileDoc } from "./useProfileDoc";

// Bridges Profile.themePreference (cross-device source of truth) into the
// shared ThemeContext once the user is signed in. Renders only its children:
// it fetches the profile doc (useProfileDoc's own effect) and forwards the
// themePreference field via syncRemote, which is a no-op once the user has
// picked something this session (see ThemeContext.tsx). Mounted inside
// App.tsx's signed_in branch so the profile fetch only happens post sign-in.
// #182: the same read also gives the shell the athlete's unit, so the bell
// does not open another GET /api/profiles for it. Read once per sign-in: a
// unit changed in Preferences shows here after a reload.
const UnitContext = createContext<UnitsPreference>(displayUnitOf(null));
export const useProfileUnit = (): UnitsPreference => useContext(UnitContext);

export function ThemeProfileBridge({ children }: { children?: ReactNode }) {
  const { doc } = useProfileDoc();
  const ctx = useThemePreferenceContext();
  const themePreference = doc?.["themePreference"];

  useEffect(() => {
    ctx?.syncRemote(themePreference as Partial<ThemePreference> | null | undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themePreference]);

  return <UnitContext.Provider value={displayUnitOf(doc)}>{children}</UnitContext.Provider>;
}
